import Phaser from "phaser";
import type { WorldUiModule } from "../hook.ts";
import { renderedView } from "../mount.ts";
import { createSharpZoomControl, type SharpZoomControl } from "./control.ts";
import { planSharpZoom } from "./plan.ts";
import { createResampleQuad, createResampleUniforms, writeResampleUniforms } from "./resample.ts";

/**
 * Sharp scaling for an in-between world zoom (P1.9-T5, ADR-061 decision 5), wired through the world-UI
 * registration hook so it edits neither `scene.ts` nor `main.ts`. Whenever the world camera's zoom is not a
 * whole number, the world layer is drawn at the next whole zoom into a buffer (a `DynamicTexture` whose camera
 * the plan places each frame), where every source pixel is a whole block of texels, and a resample quad
 * (`resample.ts`) box-averages that buffer onto the canvas; the world camera then draws nothing. At a whole
 * zoom the module stands aside and the world camera draws the world as before.
 *
 * The world camera skips the world layer through a render step put first on the layer, which returns without
 * drawing when the camera drawing is the world camera and passes every other camera, the buffer's among them,
 * on to the layer's own steps. The layer's `cameraFilter` is left alone: Phaser's input asks each object's
 * `willRender`, which asks the object's layer, so a filter set for the world camera would leave every world
 * object deaf to the pointer.
 *
 * The world camera keeps the in-between zoom, so the scene's centring and device-pixel snap, the projection of
 * world-attached UI and pointer input, hit tests on world objects included, all see the zoom the player sees. The
 * buffer is drawn after the scene's update, from the world camera's view as it will render (`plan.ts`). Its camera
 * takes the world camera's scroll, which Phaser multiplies by each object's scroll factor, and the pivot the plan
 * gives, so an object whose scroll factor is not 1 lands where the world camera puts it while the world's grid
 * stays on whole texels. The resample quad is the first object in the UI layer, so the UI camera draws it straight
 * onto the canvas before any UI, with no framebuffer of its own in between. It covers the canvas and blends as any
 * quad does, so where the world leaves the buffer clear the canvas's clear colour shows, as it does under the world
 * camera.
 *
 * @example
 * ```ts
 * const control = createSharpZoomControl();
 * export const page = { id: "sharp", title: "Sharp zoom", surfaces: [uiAtlas, sharpZoom(control)] };
 * control.enabled = false;
 * ```
 */

/**
 * The part of a game object's render steps the module reaches: the list Phaser runs, first to last, each step
 * calling the next.
 */
interface RenderStepList {
  readonly _renderSteps: Phaser.Types.GameObjects.RenderWebGLStep[] | null;
}

/**
 * The depth of the resample quad in the UI layer: below anything a surface puts there.
 */
const QUAD_DEPTH = Number.MIN_SAFE_INTEGER;

let buffers = 0;

/**
 * Makes the module.
 *
 * @param control The switch the module reads each frame and the plan it writes; one that starts enabled when
 * omitted.
 * @returns The module, to list after the UI atlas and before any surface.
 */
export function sharpZoom(control: SharpZoomControl = createSharpZoomControl()): WorldUiModule {
  return {
    id: "sharp-zoom",
    /**
     * Makes the buffer and the resample quad, hidden until the zoom is in between.
     *
     * @param context The scene, both layers, the UI scale and the world camera.
     * @returns The surface, which plans, draws and switches the resample each frame.
     * @throws Error when the game does not render with WebGL, or the buffer cannot be made.
     */
    create(context) {
      const { scene, worldLayer, worldCamera: world } = context;
      if (!(scene.sys.renderer instanceof Phaser.Renderer.WebGL.WebGLRenderer)) {
        throw new Error("sharp scaling needs the WebGL renderer");
      }
      buffers += 1;
      const buffer = scene.textures.addDynamicTexture(`sharp-zoom-buffer-${buffers}`, 2, 2);
      if (buffer === null) {
        throw new Error("the sharp zoom buffer could not be made");
      }
      const uniforms = createResampleUniforms();
      const quad = context.add(createResampleQuad(scene, buffer, uniforms).setDepth(QUAD_DEPTH).setVisible(false));
      const view = { width: 0, height: 0, originX: 0, originY: 0, zoom: 1, scrollX: 0, scrollY: 0 };
      let drawing = false;

      /**
       * The world layer's first render step: while the resample draws, the world camera draws nothing of the
       * layer; any other camera, and the world camera at a whole zoom, runs the layer's next step.
       *
       * @param renderer The renderer.
       * @param gameObject The world layer.
       * @param drawingContext The drawing context, whose camera is drawing.
       * @param parentMatrix The parent matrix, if any.
       * @param renderStep This step's index in the layer's steps.
       * @param displayList The list being drawn.
       * @param displayListIndex The layer's index in that list.
       */
      const skipForWorldCamera: Phaser.Types.GameObjects.RenderWebGLStep = (renderer, gameObject, drawingContext, parentMatrix, renderStep = 0, displayList, displayListIndex) => {
        if (drawing && drawingContext.camera === world) {
          return;
        }
        const next = (gameObject as unknown as RenderStepList)._renderSteps?.[renderStep + 1];
        next?.(renderer, gameObject, drawingContext, parentMatrix, renderStep + 1, displayList, displayListIndex);
      };
      worldLayer.addRenderStep(skipForWorldCamera, 0);

      /**
       * Hands the world back to the world camera.
       */
      const standAside = (): void => {
        quad.setVisible(false);
        drawing = false;
      };

      return {
        /**
         * Plans the frame from the world camera as it will render; when the zoom is in between and the switch is
         * on, draws the world into the buffer at the whole zoom and sizes the quad over the canvas.
         */
        update() {
          const plan = control.plan;
          if (control.enabled) {
            planSharpZoom(renderedView(world, view), plan);
          } else {
            plan.active = false;
          }
          if (!plan.active) {
            if (drawing) {
              standAside();
            }
            return;
          }
          if (buffer.width < plan.bufferWidth || buffer.height < plan.bufferHeight || buffer.width > plan.bufferWidth + 1 || buffer.height > plan.bufferHeight + 1) {
            buffer.setSize(plan.bufferWidth, plan.bufferHeight);
            buffer.setFilter(Phaser.Textures.FilterMode.LINEAR);
          }
          const camera = buffer.camera;
          camera.setOrigin(plan.pivotX / camera.width, plan.pivotY / camera.height).setZoom(plan.renderZoom).setScroll(plan.scrollX, plan.scrollY).setRoundPixels(world.roundPixels);
          buffer.clear().draw(worldLayer).render();
          const scale = context.scale.current;
          if (quad.width !== view.width / scale || quad.height !== view.height / scale) {
            quad.setSize(view.width / scale, view.height / scale);
          }
          writeResampleUniforms(uniforms, plan, buffer.width, buffer.height, view.width, view.height);
          if (!drawing) {
            quad.setVisible(true);
            drawing = true;
          }
        },
        /**
         * Hands the world back to the world camera, takes the render step off the world layer and frees the
         * buffer.
         */
        destroy() {
          standAside();
          const steps = (worldLayer as unknown as RenderStepList)._renderSteps;
          const at = steps === null ? -1 : steps.indexOf(skipForWorldCamera);
          if (steps !== null && at !== -1) {
            steps.splice(at, 1);
          }
          buffer.destroy();
        },
      };
    },
  };
}
